import React, {useEffect, useState} from "react";
import classnames from "classnames";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import IconButton from "../../../../base/button/icon_button";
import {Edit} from "@mui/icons-material";
import {Fade, SvgIcon} from "@mui/material";
import {ReactComponent as RemoveIcon} from "../../../../../../assets/images/remove.svg";
import WarrantyAndReturnExtraFeesForm, {ExtraFeesValueTypes} from "../extra-fee-form";
import SaveOrRollback from "../../../save-or-rollback";
import {deepEqual} from "../../../../../../core/services/utils/utils";
import {Col} from "reactstrap";

const WarrantyAndReturnExtraFeesCard = ({fee, titles, openTitleManagement, remove, update}) => {
    const [editing, setEditing] = useState(false)
    const [initialValues, setInitialValues] = useState({})
    const isMounted = useIsMounted()


    /**
     * Listens to the changes in the fee and with each change:
     * sets the initialValues of the form.
     */
    useEffect(() => {
        setInitialValues({
            title: fee?.titleInfo ?? undefined,
            type: fee?.isPercent ? ExtraFeesValueTypes.percent : ExtraFeesValueTypes.price,
            minDays: fee?.minDays,
            maxDays: fee?.maxDays,
            fee: fee?.value,
        })
    }, [fee])

    /**
     * Creates the updated version of the extra fee that is to be sent to the api call and calls the api.
     * @param {any} values the form values
     * @return {Promise<boolean>}
     */
    const updateExtraFee = (values) => {
        const forApi = {
            ...fee,
            titleInfo: values.title,
            minDays: values.minDays ? parseInt(values.minDays) : undefined,
            maxDays: values.maxDays ? parseInt(values.maxDays) : undefined,
            isPercent: values.type?.id === ExtraFeesValueTypes.percent.id,
            value: values.fee,
        }
        update(forApi).then(resultFlag => {
            if (!isMounted()) return
            if (resultFlag) {
                setEditing(false)
            }
        })
    }

    /**
     * Invokes the form submission callback if and only if the values have changed.
     * @param values
     * @param initialValues
     * @param form
     */
    const saveChanges = (values, initialValues, form) => {
        if (deepEqual(values, initialValues)) {
            setEditing(false)
            return;
        }
        form.submit()
    }

    /**
     * Reverts the values of this fee.
     * @param {any} values
     * @param {formApi} form
     */
    const revertValues = (values, form) => {
        setEditing(false)
        form.reset(initialValues)
    }

    /**
     * Opens the delete confirmation modal to ask for users decision about removing this fee.
     * If the user confirms, then removes the fee.
     */
    const getUserConfirmation = () => {
        window.partner?.openConfirmationDialog(
            <div className={'p-3'}>
                <p className={'text-sm text-color-2'}>
                    Do you want to delete this extra fee?
                </p>
            </div>,
            {},
            (decision) => decision && remove(fee)
        );
    }

    return (
        <>
            <div className={classnames('extra-fees-card', {'editing': editing})}>
                <div className={'d-flex flex-column'}>
                    <Fade
                        in={!editing}
                        mountOnEnter
                        unmountOnExit
                        className={'d-flex justify-content-end align-items-center'}>
                        <div>
                            <IconButton
                                disabled={fee.editing || editing}
                                className={"mx-1 ml-2"}
                                color={'paper.secondary'}
                                onPressed={() => setEditing(true)}
                            >
                                <Edit className={"icon normal"}/>
                            </IconButton>
                            <IconButton
                                onPressed={() => getUserConfirmation()}
                                disabled={fee?.editing || editing}
                                color={'paper.error'}
                            >
                                <SvgIcon component={RemoveIcon} className='icon normal'/>
                            </IconButton>
                        </div>
                    </Fade>
                    <div className={classnames('fade-container', {
                        'expanded': editing,
                    })}>
                        <Fade in={editing}>
                            <WarrantyAndReturnExtraFeesForm
                                initialValues={initialValues}
                                openTitleManagement={openTitleManagement}
                                titles={titles}
                                onSubmit={updateExtraFee}
                            >
                                {(form, values, initialValues, hasValidationError) => {
                                    Object.values(values)
                                    return (
                                        <div className={'d-flex w-100 justify-content-end'}>
                                            <SaveOrRollback
                                                column={false}
                                                loading={fee.editing}
                                                show={!hasValidationError}
                                                onRollback={() => revertValues(values, form)}
                                                onSave={() => saveChanges(values, initialValues, form)}
                                            />
                                        </div>
                                    )
                                }}
                            </WarrantyAndReturnExtraFeesForm>
                        </Fade>
                        <Fade in={!editing}>
                            <div className={'values'}>
                                <Col xs={4} className={'cell dark'}>
                                    {
                                        !!fee.minDays || fee.minDays === 0
                                            ? <p>
                                                Min Days
                                                <span className={'ml-2'}>{fee.minDays}</span>
                                            </p>
                                            : null
                                    }
                                </Col>
                                <Col xs={8} className={'cell light'}>
                                    {
                                        !!fee.titleInfo?.title
                                            ? <p>
                                                Title
                                                <span className={'ml-2'}>{fee.titleInfo?.title}</span>
                                            </p>
                                            : null
                                    }
                                </Col>
                                <Col xs={4} className={'cell light'}>
                                    {
                                        !!fee.maxDays || fee.maxDays === 0
                                            ? <p>
                                                Max Days
                                                <span className={'ml-2'}>{fee.maxDays}</span>
                                            </p>
                                            : null
                                    }
                                </Col>
                                <Col xs={8} className={'cell dark'}>
                                    {
                                        !!fee.value
                                            ? <p>
                                                Fee
                                                {
                                                    !fee.isPercent &&
                                                    <span className={'ml-2'}>$</span>
                                                }
                                                <span className={fee.isPercent ? 'ml-2' : ''}>
                                                    {fee.value}
                                                </span>
                                                {
                                                    fee.isPercent &&
                                                    <span>%</span>
                                                }
                                            </p>
                                            : null
                                    }
                                </Col>
                            </div>
                        </Fade>
                    </div>
                </div>
            </div>
        </>
    )
}

export default WarrantyAndReturnExtraFeesCard;
